import { Button, ListingLoader } from 'common';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { selectCurrentTenant } from 'reducers/selectors';
import { fetchCurrentTenant } from 'reducers/tenant-config-slice';
import { userType } from '../utils';
import { AuthProvider } from '../contexts/auth-context';
import ThemeProvider from '../providers/themeProvider';
import { getCookie, removeCookie } from '../utils/cookies-util';
import BaseRoutes from './BaseRoutes';
import { TENANT_CONFIG_EXPIRY_DAYS } from 'constants/tenant-constants';
import NotFound from 'pages/404/not_found';
import serverDownLogo from 'assets/imgs/server_error.png';
import tenantNotFoundLogo from 'assets/imgs/not_found_display.png';

import { CustomerSupportIcon } from 'common/all-icons/custom-icons';

const ElevateRoutes = () => {
  const dispatch = useDispatch();
  const {
    tenantData,
    loading: tenantConfigLoading,
    timeStamp: tenantLastFetchedTimeStamp,
    domainUrl,
    error: tenantConfigError,
  } = useSelector(selectCurrentTenant);
  const currentUser = userType() === 'user' ? getCookie('access-token-user') : getCookie('access-token');
  const [user, setUser] = useState(!!currentUser);

  const handleLogin = async () => {
    setUser(true);
    removeCookie('firebase_new_token');
  };

  const handleLogout = () => setUser(false);

  useEffect(() => {
    const applyTheme = () => {
      const favIconUrl = tenantData?.favicon_url;
      let link = document.querySelector('link[rel="favicon icon"]');

      if (!link && !favIconUrl) {
        link = document.createElement('link');
        link.id = 'favicon';
        link.rel = 'favicon icon';
        document.head.appendChild(link);
      }
      link.href = favIconUrl;
    };

    requestAnimationFrame(applyTheme);
  }, [tenantData?.slug]);

  useEffect(() => {
    const isDomainSame = window.location.hostname === domainUrl;

    const isExpired =
      !tenantLastFetchedTimeStamp ||
      dayjs().diff(dayjs(tenantLastFetchedTimeStamp), 'day') >= TENANT_CONFIG_EXPIRY_DAYS;

    if (!tenantData?.slug || isExpired || !isDomainSame) {
      dispatch(fetchCurrentTenant(window.location.hostname));
    }
  }, []);

  if (tenantConfigLoading)
    return (
      <ListingLoader
        loaderHeight="200px"
        loaderWidth="200px"
        color={tenantData?.style_sheet?.ant_variables?.primary_color_light ?? ''}
      />
    );

  if (tenantConfigError?.code === 500)
    return (
      <NotFound
        title="500 Server Down"
        description={
          'Something went wrong on our side. We are working to fix it. Please refresh the page or check back later.'
        }
        actionButton={
          <Button className={'page-button mt-5'} onClick={() => window.location.reload()}>
            {'Reload Page'}
          </Button>
        }
        logo={serverDownLogo}
      />
    );

  if (tenantConfigError?.code === 404)
    return (
      <NotFound
        title="Tenant Configuration Error"
        description={'This Tenant is not configured properly. Please contact support for assistance..'}
        actionButton={
          <Button
            className={'page-button mt-5'}
            onClick={() => (window.location.href = 'mailto:support@joinelevate.com?subject=Tenant Configuration Error')}
          >
            <CustomerSupportIcon />
            {'Customer Support'}
          </Button>
        }
        logo={tenantNotFoundLogo}
      />
    );

  return (
    <AuthProvider value={{ handleLogout }}>
      <Router>
        <ThemeProvider>
          <Toaster
            toastOptions={{
              style: {
                zIndex: 99999,
              },
            }}
          />
          <BaseRoutes user={user} handleLogin={handleLogin} handleLogut={handleLogout} />
        </ThemeProvider>
      </Router>
    </AuthProvider>
  );
};

export default ElevateRoutes;
